<template>
  <br />
  <div style="padding:5px" class="row">
    <div class="col-lg-4 col-md-4 mb-3" v-for="metric in metricCards" :key="metric.title">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">{{ metric.title }}</h5>
          <p class="card-text">
            <font-awesome-icon v-if="metricsLoading" icon="fa-solid fa-sync fa-10x" spin />
            <template v-else>{{ metric.value }}</template>
          </p>
        </div>
      </div>
    </div>

    <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
      <label for="search" class="form-label mt-2 me-2">Keyword</label>
      <input id="search" class="form-control" placeholder="Creator, tier.." v-model="keyword" @input="searchKeyword" />
    </div>
    <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
      <label for="status" class="form-label me-1 mt-2">Status</label>
      <select id="status" class="form-control" v-model="status" @change="perPageFilter">
        <option value="all">All</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
    </div>
    <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
      <label for="per-page" class="form-label me-1 mt-2">Per page</label>
      <select id="per-page" class="form-control" v-model.number="perPage" @change="perPageFilter">
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
      </select>
    </div>

    <div class="alert alert-danger" role="alert" v-if="error">
      {{ error }}
    </div>

    <table class="table">
      <thead>
      <tr>
        <th scope="col"># ID</th>
        <th scope="col">Creator</th>
        <th scope="col">Tier</th>
        <th scope="col">Discount</th>
        <th scope="col">Status</th>
        <th scope="col">Sent</th>
        <th scope="col">Used</th>
        <th scope="col">Conversion rate</th>
        <th scope="col">Actions</th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="loading">
        <td colspan="9" class="text-center"><font-awesome-icon icon="fa-solid fa-sync fa-10x" spin /></td>
      </tr>
      <tr v-else-if="winBacks.length === 0">
        <td colspan="9" class="text-center">No win-backs found</td>
      </tr>
      <template v-else>
        <tr v-for="winBack in winBacks" :key="winBack.id">
          <th scope="row">{{ winBack.id }}</th>
          <td>{{ winBack.channel?.name ?? '-' }}</td>
          <td>{{ winBack.tier?.name ?? '-' }}</td>
          <td>{{ winBack.discount_percentage }}% for {{ winBack.discount_duration_months }} mo. <br/>
            Days after churn: {{ winBack.days_after_churn }} d.
          </td>
          <td>
            <span v-if="winBack.is_active" class="badge badge-success">Active</span>
            <span v-else class="badge badge-secondary">Inactive</span>
          </td>
          <td>{{ winBack.sent_count ?? 0 }}</td>
          <td>{{ winBack.used_count ?? 0 }}</td>
          <td>{{ winBack.conversion_rate ?? 0 }}%</td>
          <td>
            <button type="button" class="btn btn-sm btn-info" @click="showDetails(winBack)">
              More details
            </button>
          </td>
        </tr>
      </template>
      </tbody>
    </table>
    <div class="d-flex justify-content-between" v-if="!loading && totalRecords >= 1">
      <div class="p-2">
        <p>Total win-backs: {{ totalRecords }}</p>
      </div>
      <div class="p-2">
        <nav v-if="totalPages > 1" aria-label="Page navigation">
          <ul class="pagination">
            <li class="page-item" :class="{ disabled: currentPage <= 1 }">
              <a @click="paginate(currentPage - 1)" class="page-link">Previous</a>
            </li>
            <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
              <a @click="paginate(page)" class="page-link">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
              <a @click="paginate(currentPage + 1)" class="page-link">Next</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import _debounce from 'lodash/debounce';
import Swal from 'sweetalert2';
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
  components: {FontAwesomeIcon},
  data() {
    return {
      loading: true,
      metricsLoading: true,
      metrics: null,
      winBacks: [],
      keyword: '',
      status: 'all',
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      perPage: 10,
      error: ''
    }
  },

  computed: {
    visiblePages() {
      const from = Math.max(1, this.currentPage - 2);
      const to = Math.min(this.totalPages, this.currentPage + 2);
      return Array.from({length: to - from + 1}, (_, i) => from + i);
    },
    metricCards() {
      const m = this.metrics ?? {};
      return [
        {title: 'Win-backs created', value: m.win_backs_created ?? 0},
        {title: 'Active win-backs', value: m.active_win_backs ?? 0},
        {title: 'Win-backs sent', value: m.win_backs_sent ?? 0},
        {title: 'Win-backs used', value: m.win_backs_used ?? 0},
        {title: 'Creators using win-back', value: m.channels_used ?? 0},
        {title: 'Conversion rate', value: `${m.conversion_rate ?? 0}%`},
      ];
    },
  },

  mounted() {
    this.getMetrics();
    this.getWinBacks();
  },

  methods: {
    debouncedGetWinBacks: _debounce(function () {
      this.error = '';
      axios.get(`/admin/win-back-offers/get?page=${this.currentPage}&per_page=${this.perPage}&keyword=${encodeURIComponent(this.keyword.trim())}&status=${this.status}`).then(response => {
        this.winBacks = response.data.data;
        this.totalPages = response.data.totalPages;
        this.totalRecords = response.data.totalRecords;
        this.loading = false;
      }).catch(() => {
        this.loading = false;
        this.error = 'Failed to load win-backs.';
      });
    }, 500),
    getMetrics() {
      this.metricsLoading = true;
      axios.get('/admin/win-back-offers/metrics').then(response => {
        this.metrics = response.data;
      }).catch(() => {
        this.error = 'Failed to load win-back metrics.';
      }).finally(() => {
        this.metricsLoading = false;
      });
    },
    getWinBacks() {
      this.loading = true;
      this.debouncedGetWinBacks();
    },
    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value ?? '';
      return div.innerHTML;
    },
    showDetails(winBack) {
      Swal.fire({
        title: `Win-back details #${winBack.id}`,
        html: `<div style="text-align:left">
          <div><strong>Created at:</strong> ${winBack.created_at}</div>
          <div><strong>Updated at:</strong> ${winBack.updated_at}</div>
          <div class="mt-2"><strong>Email text:</strong></div>
          <div class="win-back-email-text">${winBack.email_text ?? '-'}</div>
        </div>`,
        width: 450,
        confirmButtonText: 'Close',
        didOpen: (popup) => {
          popup.querySelectorAll('.win-back-email-text img').forEach((img) => {
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
          });
        },
      });
    },
    searchKeyword() {
      this.currentPage = 1;
      this.getWinBacks();
    },
    perPageFilter() {
      this.currentPage = 1;
      this.getWinBacks();
    },
    paginate(page) {
      if (page < 1 || page > this.totalPages) {
        return;
      }
      this.currentPage = page;
      this.getWinBacks();
    },
  }
}
</script>
